import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Mail, KeyRound, AlertCircle } from "lucide-react";
import { useLang } from "../lang";
import { LOGO_MAIN } from "../content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#f5f5f7] rounded-xl px-4 py-3.5 text-[15px] text-[#1d1d1f] placeholder:text-[#6e6e73] outline-none focus:ring-2 focus:ring-[#1D3F94]/40 transition-shadow";

export const fmtErr = (detail, fallback) => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return fallback;
};

function Shell({ children, testId }) {
  return (
    <main className="min-h-screen bg-white flex items-center justify-center px-6 py-20" data-testid={testId}>
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }} className="w-full max-w-[460px] text-center">
        <Link to="/" className="inline-flex mb-10"><img src={LOGO_MAIN} alt="HGW Industry" className="h-8" /></Link>
        <div className="text-[12px] uppercase tracking-[0.14em] text-[#1D3F94] mb-3">Area riservata</div>
        {children}
      </motion.div>
    </main>
  );
}

function StatusIcon({ kind }) {
  const map = { ok: [Check, "bg-[#1D3F94]/10 text-[#1D3F94]"], error: [AlertCircle, "bg-red-50 text-red-600"], mail: [Mail, "bg-[#1D3F94]/10 text-[#1D3F94]"], key: [KeyRound, "bg-[#1D3F94]/10 text-[#1D3F94]"], no: [X, "bg-[#f5f5f7] text-[#1d1d1f]"] };
  const [Icon, cls] = map[kind];
  return <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6 ${cls}`}><Icon size={24} strokeWidth={2} /></div>;
}

function Message({ icon, title, text, children, testId }) {
  return (
    <div data-testid={testId}>
      <StatusIcon kind={icon} />
      <h1 className="text-[28px] md:text-[32px] font-semibold tracking-[-0.02em] text-[#1d1d1f] text-balance">{title}</h1>
      {text && <p className="mt-4 text-[15px] text-[#6e6e73] leading-relaxed text-pretty">{text}</p>}
      {children && <div className="mt-8 flex flex-col items-center gap-4">{children}</div>}
    </div>
  );
}

export function VerificaEmail() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "loading" });
  useEffect(() => {
    axios.post(`${API}/b2b/verify-email`, { token: params.get("token") || "" })
      .then(() => setState({ status: "ok" }))
      .catch((e) => setState({ status: "error", msg: fmtErr(e?.response?.data?.detail, "Link non valido.") }));
  }, [params]);
  return (
    <Shell testId="verify-page">
      {state.status === "loading" && <p className="text-[#6e6e73]" data-testid="verify-loading">Verifica in corso…</p>}
      {state.status === "ok" && (
        <Message icon="ok" testId="verify-ok" title="Email confermata" text="Grazie. La tua richiesta è ora all'esame del nostro team: riceverai una email appena l'accesso sarà approvato, con il link per scegliere la password.">
          <Link to="/" className="btn-pill btn-primary">Torna al sito</Link>
        </Message>
      )}
      {state.status === "error" && (
        <Message icon="error" testId="verify-error" title="Impossibile confermare" text={state.msg}>
          <Link to="/richiedi-accesso" className="btn-pill btn-primary">Ripeti la registrazione</Link>
        </Message>
      )}
    </Shell>
  );
}

function RequestSummary({ r }) {
  const rows = [["Ragione sociale", r.ragione_sociale], ["Partita IVA", r.partita_iva], ["Referente", `${r.referente_nome} ${r.referente_cognome}`], ["Email verificata", r.email], ["Telefono", r.telefono], ["Messaggio", r.note]].filter(([, v]) => v);
  return (
    <dl className="mt-8 text-left bg-[#f5f5f7] rounded-2xl p-5 space-y-3" data-testid="decision-summary">
      {rows.map(([k, v]) => (
        <div key={k} className="grid grid-cols-[130px_1fr] gap-3 text-[14px]">
          <dt className="text-[#6e6e73]">{k}</dt><dd className="text-[#1d1d1f] break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Decisione() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const preset = params.get("azione") === "rifiuta" ? "reject" : "approve";
  const [req, setReq] = useState(null);
  const [state, setState] = useState({ status: "loading" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/b2b/decision`, { params: { token } })
      .then(({ data }) => { setReq(data); setState({ status: data.status === "pending" ? "ready" : "handled" }); })
      .catch((e) => setState({ status: "error", msg: fmtErr(e?.response?.data?.detail, "Link non valido.") }));
  }, [token]);

  const decide = async (action) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/b2b/decision`, { token, action });
      setState({ status: "done", action, emailSent: data.email_sent });
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail, "Operazione non riuscita."));
    } finally { setBusy(false); }
  };

  return (
    <Shell testId="decision-page">
      {state.status === "loading" && <p className="text-[#6e6e73]">Caricamento…</p>}
      {state.status === "error" && <Message icon="error" testId="decision-error" title="Link non utilizzabile" text={state.msg} />}
      {state.status === "handled" && <Message icon="ok" testId="decision-handled" title="Richiesta già gestita" text={`Stato attuale: ${req.status === "approved" ? "approvata" : "rifiutata"}.`} />}
      {state.status === "ready" && (
        <div data-testid="decision-ready">
          <h1 className="text-[28px] md:text-[32px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">{preset === "approve" ? "Approvare l'accesso?" : "Rifiutare l'accesso?"}</h1>
          <p className="mt-3 text-[15px] text-[#6e6e73]">Controlla i dati e conferma la tua scelta.</p>
          <RequestSummary r={req} />
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={() => decide(preset)} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="decision-confirm">
              {preset === "approve" ? "Conferma approvazione" : "Conferma rifiuto"}
            </button>
            <button onClick={() => decide(preset === "approve" ? "reject" : "approve")} disabled={busy} className="text-[14px] text-[#6e6e73] hover:text-[#1d1d1f] px-3 py-2" data-testid="decision-switch">
              {preset === "approve" ? "Rifiuta invece" : "Approva invece"}
            </button>
          </div>
        </div>
      )}
      {state.status === "done" && (
        <Message
          icon={state.action === "approve" ? "ok" : "no"}
          testId="decision-done"
          title={state.action === "approve" ? "Accesso approvato" : "Richiesta rifiutata"}
          text={state.action === "approve"
            ? (state.emailSent ? "Il professionista ha ricevuto una email per impostare la password." : "Approvata, ma l'email non è partita: contatta direttamente il professionista.")
            : "Il professionista è stato informato via email."}
        />
      )}
    </Shell>
  );
}

const pwOk = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);

export function ImpostaPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/set-password/check`, { params: { token } })
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(fmtErr(e?.response?.data?.detail, "Link non valido.")));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwOk(pw)) return toast.error("Almeno 8 caratteri, con lettere e numeri.");
    if (pw !== pw2) return toast.error("Le due password non coincidono.");
    setBusy(true);
    try {
      await axios.post(`${API}/auth/set-password`, { token, password: pw });
      toast.success("Password impostata. Ora puoi accedere.");
      navigate("/login");
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, "Operazione non riuscita."));
    } finally { setBusy(false); }
  };

  return (
    <Shell testId="setpw-page">
      {error && (
        <Message icon="error" testId="setpw-error" title="Link non utilizzabile" text={error}>
          <Link to="/area-riservata/password-dimenticata" className="btn-pill btn-primary">Richiedi un nuovo link</Link>
        </Message>
      )}
      {!error && !info && <p className="text-[#6e6e73]">Caricamento…</p>}
      {info && (
        <div>
          <StatusIcon kind="key" />
          <h1 className="text-[28px] md:text-[32px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">Scegli la password</h1>
          <p className="mt-3 text-[15px] text-[#6e6e73]">{info.email}</p>
          <form onSubmit={submit} className="mt-8 space-y-3 text-left" data-testid="setpw-form">
            <input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nuova password" className={inputCls} data-testid="setpw-input" />
            <input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Ripeti la password" className={inputCls} data-testid="setpw-input-confirm" />
            <p className={`text-[12px] px-1 ${pw && !pwOk(pw) ? "text-[#c15c00]" : "text-[#6e6e73]"}`}>Almeno 8 caratteri, con lettere e numeri.</p>
            <button type="submit" disabled={busy} className="btn-pill btn-primary w-full justify-center disabled:opacity-60 !mt-5" data-testid="setpw-submit">
              {busy ? "…" : "Salva e continua"}
            </button>
          </form>
        </div>
      )}
    </Shell>
  );
}

export function PasswordDimenticata() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, "Inserisci un'email valida."));
    } finally { setBusy(false); }
  };
  return (
    <Shell testId="forgot-page">
      {sent ? (
        <Message icon="mail" testId="forgot-sent" title="Controlla la tua email" text="Se l'indirizzo corrisponde a un account attivo, riceverai a breve un link per reimpostare la password (valido 2 ore).">
          <Link to="/login" className="a-link">{lang === "it" ? "Torna all'accesso" : "Back to sign in"} <span className="chev">›</span></Link>
        </Message>
      ) : (
        <div>
          <h1 className="text-[28px] md:text-[32px] font-semibold tracking-[-0.02em] text-[#1d1d1f]">Password dimenticata</h1>
          <p className="mt-3 text-[15px] text-[#6e6e73]">Inserisci l'email del tuo account: ti invieremo un link per sceglierne una nuova.</p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} data-testid="forgot-input-email" />
            <button type="submit" disabled={busy} className="btn-pill btn-primary w-full justify-center disabled:opacity-60" data-testid="forgot-submit">{busy ? "…" : "Invia il link"}</button>
          </form>
          <Link to="/login" className="a-link inline-flex mt-8">← Torna all'accesso</Link>
        </div>
      )}
    </Shell>
  );
}
